import { apiClient } from '@/api/core';
import { API_CONFIG } from '@/api/core/apiConfig';
import { buildQueryString } from '@/api/shared/queryBuilder';
import type { ApiResponse , LC_PagedResult } from '@/api/shared';
import { FRACTIONAL_LEAVE_STATUS_FILTER } from './fractionalLeave.types';
import type {
  LC_FractionalLeaveItemOut,
  LC_FractionalLeaveToggleStatusIn,
  LC_FractionalLeaveToggleStatusOut,
  LC_FractionalLeaveUpdateIn,
  LC_FractionalLeaveUpdateOut,
  LC_FractionalLeaveCreateIn,
  LC_FractionalLeaveCreateOut,
  FractionalLeave,
  FractionalLeavePage,
  FractionalLeaveListParams,
} from './fractionalLeave.types';

import { mapFractionalLeavePagedResultToModel, mapFractionalLeaveToModel } from './fractionalLeave.mapper';


export class FractionalLeaveService {
  /** Fetch paginated fractional-leave list */
  async list(params: FractionalLeaveListParams): Promise<ApiResponse<FractionalLeavePage>> {
    const statusParam: boolean | undefined =
      params.statusFilter === FRACTIONAL_LEAVE_STATUS_FILTER.ACTIVE
        ? true
        : params.statusFilter === FRACTIONAL_LEAVE_STATUS_FILTER.INACTIVE
          ? false
          : undefined;

    const queryString = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      status: statusParam,
      orderBy: params.sort?.key,
      orderDirection: params.sort?.direction?.toUpperCase(),
    });

    const response = await apiClient.get<LC_PagedResult<LC_FractionalLeaveItemOut>>(
      `${API_CONFIG.ENDPOINTS.FRACTIONAL_LEAVE.LIST}${queryString}`,
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: mapFractionalLeavePagedResultToModel(response.data),
      };
    }

    return { success: false, data: null, error: response.error };
  }

  /** Create a new fractional-leave record via [BE-002] */
  async create(payload: LC_FractionalLeaveCreateIn): Promise<ApiResponse<FractionalLeave>> {
    const response = await apiClient.post<LC_FractionalLeaveCreateOut>(
      API_CONFIG.ENDPOINTS.FRACTIONAL_LEAVE.CREATE,
      payload,
    );

    if (response.success && response.data) {
      return { success: true, data: mapFractionalLeaveToModel(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }

  /** Toggle the active status of a fractional-leave record via [BE-004] */
  async toggleStatus(id: number, isActive: boolean): Promise<ApiResponse<FractionalLeave>> {
    const payload: LC_FractionalLeaveToggleStatusIn = { isActive };
    const response = await apiClient.patch<LC_FractionalLeaveToggleStatusOut>(
      API_CONFIG.ENDPOINTS.FRACTIONAL_LEAVE.TOGGLE_STATUS(id),
      payload,
    );

    if (response.success && response.data) {
      return { success: true, data: mapFractionalLeaveToModel(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }

  /** Update the fields of an existing fractional-leave record */
  async update(
    id: number,
    payload: LC_FractionalLeaveUpdateIn,
  ): Promise<ApiResponse<FractionalLeave>> {
    const response = await apiClient.put<LC_FractionalLeaveUpdateOut>(
      API_CONFIG.ENDPOINTS.FRACTIONAL_LEAVE.UPDATE(id),
      payload,
    );

    if (response.success && response.data) {
      return { success: true, data: mapFractionalLeaveToModel(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }

  /** Export fractional-leave list as Excel file via [BE-005] */
  async export(params: FractionalLeaveListParams)
    : Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const statusParam: boolean | undefined =
      params.statusFilter === FRACTIONAL_LEAVE_STATUS_FILTER.ACTIVE
        ? true
        : params.statusFilter === FRACTIONAL_LEAVE_STATUS_FILTER.INACTIVE
          ? false
          : undefined;

    const queryString = buildQueryString({
      searchText: params.searchText,
      status: statusParam,
      orderBy: params.sort?.key,
      orderDirection: params.sort?.direction?.toUpperCase(),
    });

    return apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.FRACTIONAL_LEAVE.EXPORT}${queryString}`,
      { method: 'GET' },
    );
  }
}

export const fractionalLeaveService = new FractionalLeaveService();
